import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { SliceCaseReducers } from '@reduxjs/toolkit/src/createSlice';

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import SiteApi from '../../Api/SiteApi/SiteApi';
import { GameModule } from '../../utils/GameModule';
import { useGlobalStoreSelector } from '../GlobalStore';

import FetchInterface from './FetchInterface';

const fetchModules = createAsyncThunk(
    'modules/fetch',
    async (): Promise<{ enabledModules: string[] }> => {
        return SiteApi.getModules();
    }
);

interface ModulesInterface extends FetchInterface {
    enabledModules: GameModule[];
}

const ModulesSlice = createSlice<ModulesInterface, SliceCaseReducers<ModulesInterface>>({
    name: 'modules',
    initialState: {
        isLoading: false,
        isFetched: false,
        enabledModules: [],
    },
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchModules.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(fetchModules.fulfilled, (state, action) => {
            state.enabledModules = action.payload.enabledModules.filter((m): m is GameModule =>
                Object.values(GameModule).includes(m as GameModule)
            );

            state.isLoading = false;
            state.isFetched = true;
        });

        builder.addCase(fetchModules.rejected, (state) => {
            state.isLoading = false;
            state.isFetched = false;
        });
    },
});

export const useModules = () => {
    const dispatch = useDispatch();

    const { enabledModules, isLoading, isFetched } = useGlobalStoreSelector((state) => state.modules);

    useEffect(() => {
        if (!isFetched && !isLoading) {
            dispatch(fetchModules());
        }
    }, [dispatch, isFetched, isLoading]);

    const isModuleEnabled = (module: GameModule): boolean => {
        return enabledModules.includes(module);
    };

    return {
        enabledModules,
        isLoading,
        isModuleEnabled,
    };
};

export default ModulesSlice.reducer;
